import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    ArrowDownTrayIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    WalletIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";
import {
    averageBy,
    buildBreakdownRows,
    buildCurrencyBreakdownRows,
    collectPaginatedRows,
    countBy,
    createExportFileName,
    exportRide2FiveExcelReport,
    sumBy,
} from "@/lib/export/excel";
import { getWallets } from "../api/walletPaymentsApi";
import type { WalletsQueryParams, WalletStatus, UserType } from "../types";

// ============================================================================
// Constants
// ============================================================================

const statusOptions: { value: WalletStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "ACTIVE", label: "Active" },
    { value: "FROZEN", label: "Frozen" },
    { value: "CLOSED", label: "Closed" },
];

const userTypeOptions: { value: UserType | ""; label: string }[] = [
    { value: "", label: "All Types" },
    { value: "RIDER", label: "Rider" },
    { value: "DRIVER", label: "Driver" },
];

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FROZEN: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        CLOSED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${s === "ACTIVE" ? "bg-emerald-500" : s === "FROZEN" ? "bg-blue-500" : s === "CLOSED" ? "bg-rose-500" : "bg-slate-400"}`} />
            {s}
        </span>
    );
};

const UserTypeBadge = ({ type }: { type: string }) => {
    const styles: Record<string, string> = {
        RIDER: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
        DRIVER: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[type] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {type}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const WalletsRoute = () => {
    const navigate = useNavigate();
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [filters, setFilters] = useState<WalletsQueryParams>({
        page: 0,
        size: 20,
        status: "",
        userType: "",
        search: "",
    });
    const [showFilters, setShowFilters] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ["wallets", filters],
        queryFn: () => getWallets(filters),
    });

    const wallets = data?.data?.wallets ?? [];
    const totalPages = data?.data?.totalPages ?? 0;
    const totalElements = data?.data?.totalElements ?? 0;
    const currentPage = filters.page ?? 0;

    const handleExport = async () => {
        try {
            setIsExporting(true);
            setExportError("");

            const walletRows = await collectPaginatedRows({
                fetchPage: (page) =>
                    getWallets({
                        ...filters,
                        page,
                        size: 100,
                    }),
                getRows: (response) => response.data.wallets,
                getTotalPages: (response) => response.data.totalPages,
            });

            const statusCounts = countBy(walletRows, (wallet) => wallet.status);
            const userTypeCounts = countBy(walletRows, (wallet) => wallet.userType);
            const totalBalance = sumBy(walletRows, (wallet) => wallet.balance);
            const averageBalance = averageBy(walletRows, (wallet) => wallet.balance) ?? 0;
            const currencyBreakdown = buildCurrencyBreakdownRows(
                walletRows,
                (wallet) => wallet.currency,
                (wallet) => wallet.balance
            );

            await exportRide2FiveExcelReport({
                title: "Wallets Export",
                subtitle:
                    "Filtered wallet export with balance, owner type, and wallet status analysis.",
                fileName: createExportFileName("wallets_export"),
                filters,
                rows: walletRows,
                metrics: [
                    { label: "Filtered Wallets", value: walletRows.length },
                    {
                        label: "Total Balance",
                        value: totalBalance.toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }),
                    },
                    {
                        label: "Average Balance",
                        value: averageBalance.toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }),
                    },
                ],
                sections: [
                    {
                        title: "Wallet Status Breakdown",
                        rows: buildBreakdownRows(statusCounts),
                    },
                    {
                        title: "Owner Type Breakdown",
                        rows: buildBreakdownRows(userTypeCounts),
                    },
                    {
                        title: "Balance By Currency",
                        rows: currencyBreakdown,
                    },
                ],
                columns: [
                    { header: "Wallet ID", width: 38, value: (wallet) => wallet.walletId },
                    { header: "User ID", width: 38, value: (wallet) => wallet.userId },
                    { header: "First Name", width: 18, value: (wallet) => wallet.firstName },
                    { header: "Last Name", width: 18, value: (wallet) => wallet.lastName },
                    { header: "Email", width: 28, value: (wallet) => wallet.email },
                    { header: "User Type", width: 14, value: (wallet) => wallet.userType },
                    { header: "Status", width: 14, value: (wallet) => wallet.status },
                    {
                        header: "Balance",
                        width: 16,
                        alignment: "right",
                        value: (wallet) => wallet.balance,
                        numFmt: "#,##0.00",
                    },
                    { header: "Currency", width: 12, value: (wallet) => wallet.currency },
                    {
                        header: "Last Activity",
                        width: 22,
                        value: (wallet) =>
                            wallet.lastTransactionAt
                                ? formatDate(wallet.lastTransactionAt)
                                : "—",
                    },
                    { header: "Created At", width: 22, value: (wallet) => formatDate(wallet.createdAt) },
                ],
                dataSheetName: "Wallets",
            });
        } catch (error) {
            setExportError(extractApiError(error));
        } finally {
            setIsExporting(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6">
            {/* Toolbar */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search by email or name…"
                        value={filters.search}
                        onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 0 }))}
                        className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-primary-500 dark:focus:ring-primary-900/30"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
                        <FunnelIcon className="mr-1.5 h-4 w-4" /> Filters
                    </Button>
                    <Button size="sm" onClick={handleExport} disabled={isExporting}>
                        <ArrowDownTrayIcon className="mr-1.5 h-4 w-4" />
                        {isExporting ? "Exporting..." : "Export Excel"}
                    </Button>
                </div>
            </div>

            {exportError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                    {exportError}
                </div>
            ) : null}

            {/* Filters */}
            {showFilters && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as WalletStatus | "", page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            >
                                {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">User Type</label>
                            <select
                                value={filters.userType}
                                onChange={(e) => setFilters((f) => ({ ...f, userType: e.target.value as UserType | "", page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            >
                                {userTypeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div className="flex items-end">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setFilters({ page: 0, size: 20, status: "", userType: "", search: "" })}
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
                    </div>
                ) : error ? (
                    <div className="py-20 text-center text-sm text-rose-500">{String(error)}</div>
                ) : wallets.length === 0 ? (
                    <div className="py-20 text-center">
                        <WalletIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No wallets found.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-800/50">
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">User</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Type</th>
                                    <th className="px-4 py-3 text-right font-medium text-slate-500 dark:text-slate-400">Balance</th>
                                    <th className="px-4 py-3 text-center font-medium text-slate-500 dark:text-slate-400">Status</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Last Activity</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Created</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {wallets.map((wallet) => (
                                    <tr
                                        key={wallet.walletId}
                                        onClick={() => navigate(`/wallet-payments/wallets/${wallet.walletId}`)}
                                        className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                                                    <UserCircleIcon className="h-5 w-5 text-slate-400" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900 dark:text-white">
                                                        {wallet.firstName} {wallet.lastName}
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">{wallet.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <UserTypeBadge type={wallet.userType} />
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-white">
                                            {formatCurrency(wallet.balance, wallet.currency)}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <StatusBadge status={wallet.status} />
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                                            {formatDate(wallet.lastTransactionAt)}
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                                            {formatDate(wallet.createdAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 dark:border-slate-800">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Page {currentPage + 1} of {totalPages} · {totalElements} wallets
                        </p>
                        <div className="flex gap-1">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setFilters((f) => ({ ...f, page: currentPage - 1 }))}
                                disabled={currentPage === 0}
                            >
                                <ChevronLeftIcon className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setFilters((f) => ({ ...f, page: currentPage + 1 }))}
                                disabled={currentPage >= totalPages - 1}
                            >
                                <ChevronRightIcon className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
